import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IGutterAsMarginProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { Size, SIZES } from '../../fundamentals/sizes/sizes';
import { IActionCssProps, useActionCss } from '../../fundamentals/actions';
import { useFilterColourCss } from '../../fundamentals/colours/filter';
import { MATERIAL_ICONS, MATERIAL_ICONS_TYPES, MaterialIcon, MaterialIconType } from './icons';
import { SEVERITY_TYPES, SeverityType, STATUS_COLOURS, StatusColour } from '../../fundamentals/colours/variants';
import { EoraAssetsContext } from '../../themes/EoraThemeProvider';
import { BaseImg } from '../../common/BaseElements';

export interface IIconSharedProps extends IGutterAsMarginProps {
  name?: MaterialIcon;
  type?: MaterialIconType;
  variant?: StatusColour;
  severityType?: SeverityType;
}

export type IconSize = Size;

export interface IIconProps extends IIconSharedProps, Pick<React.HTMLProps<HTMLImageElement>, 'key'> {
  size?: IconSize;
  colour?: string;
}

const DEFAULT_ICON = MATERIAL_ICONS.InfoOutline;
export const DEFAULT_ICON_SIZE = SIZES.X_SMALL;
export const DEFAULT_ICON_TYPE = MATERIAL_ICONS_TYPES.ROUND;

export const generateIconUrl = (iconKey: MaterialIcon, type: MaterialIconType = DEFAULT_ICON_TYPE) => `/images/i/m/${type[0].toLowerCase()}/${iconKey}.svg`;

export const useAssetIconUrl = (iconKey: MaterialIcon, type: MaterialIconType = DEFAULT_ICON_TYPE) => {
  const { baseAssetsUrl } = useContext(EoraAssetsContext);
  return `${baseAssetsUrl}${generateIconUrl(iconKey, type)}`;
};

const useSeverityColourCss = css<Pick<IIconProps, 'colour' | 'variant' | 'severityType'>>`
  ${({ colour, theme, variant, severityType }) => {
    if (!variant && !severityType) {
      return useFilterColourCss(colour ?? theme.colours.text.default);
    }

    let computedColour;
    const variantColour = variant ?? STATUS_COLOURS.NEUTRAL;
    const type = severityType ?? SEVERITY_TYPES.COLOURED;

    switch (type) {
      case SEVERITY_TYPES.BLACK: {
        computedColour = theme.colours.text.default;
        break;
      }
      case SEVERITY_TYPES.COLOURED: {
        computedColour = theme.colours.palette[variantColour];
        break;
      }
      case SEVERITY_TYPES.DARK: {
        computedColour = theme.colours.status[variantColour].colour;

        break;
      }
      default: {
        computedColour = theme.colours.base.white.colour;
      }
    }

    return useFilterColourCss(computedColour);
  }};
`;

export const useIconBaseSizeCss = css<Pick<IIconProps, 'size'>>`
  height: ${({ theme, size = DEFAULT_ICON_SIZE }) => theme.sizes[size]};
  width: ${({ theme, size = DEFAULT_ICON_SIZE }) => theme.sizes[size]};
`;

export const useIconBaseCss = css<Pick<IIconProps, 'size'> & IActionCssProps & IGutterAsMarginProps>`
  ${useIconBaseSizeCss};
  ${useActionCss};
  ${useGutterAsMarginCss()};
`;

export const Icon = styled(BaseImg).attrs<IIconProps>(({ name = DEFAULT_ICON, type = DEFAULT_ICON_TYPE, onClick, ...rest }) => {
  const src = useAssetIconUrl(name, type);

  const { generateTestId } = useTestId(rest, `Icon`);
  return {
    src,
    ...generateTestId(name),
    name: undefined,
    type: undefined
  };
})<IIconProps>`
  ${useIconBaseCss};
  ${useSeverityColourCss};
`;
